import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Animated as ReactAnimated,
  FlatList,
  Dimensions,
} from 'react-native';
import Animated, {
  Easing,
  measure,
  runOnUI,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { isTablet } from 'react-native-device-info';
import { EventRegister } from 'react-native-event-listeners';
import AppContext from '../../navigation/Context';
import colors from '../../resources/colors';
import images from '../../resources/images';
import { useToast } from 'react-native-toast-notifications';
import { addHours, format, parseISO } from 'date-fns';
import { useNavigation } from '@react-navigation/core';
import DataController from '../../controllers/DataController';
import FirebaseService from '../../controllers/FirebaseService';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { createStackNavigator } from '@react-navigation/stack';
import { FlashList } from '@shopify/flash-list';

const AnimatedKeyboardAwareScrollView = Animated.createAnimatedComponent(KeyboardAwareScrollView);

const Row = ({ day, bookmarks, notes }) => {
  const navigation = useNavigation();
  const context = useContext(AppContext);
  const { days } = context;
  const dayIndex = days.findIndex((item) => item.Date === day.Date);

  let background =
    day.Colour && (day.Colour === 'Hangings removed' ? 'grey' : day.Colour.split(/[ |\/]/gmi)[0].toLowerCase());
  const backgroundHex = DataController.colourNameToHex(background);
  const textColor = backgroundHex ? DataController.getTextColor(backgroundHex) : colors.accentWhite;

  const bookmarked = bookmarks.findIndex((bookmark) => bookmark === day.id) > -1;
  const noted = notes.findIndex((note) => note.id === day.id) > -1;

  const dropdownStyle = (day.Event && day.Event[0].option) ? DataController.getDropdownStyle(day.Event[0].option) : {};
  const eventShouldShowOption = (day.Event && day.Event[0].option) ? DataController.eventShouldShowOption(day.Event[0].option) : false;

  const time = new Date(new Date(day.Date).valueOf());

  return (
    <View style={{
      maxWidth: 1400,
      alignSelf: 'center',
      width: isTablet() || Platform.OS === 'web'
        ? Dimensions.get('window').width - 60
        : Dimensions.get('window').width - 30,
    }}>
      <TouchableOpacity
        style={styles.browseRow}
        activeOpacity={0.5}
        onPress={() => {
          !__DEV__ && FirebaseService.logEvent('day_viewed', { date: day.Date });
          navigation.navigate('Day', { day, dayIndex });
        }}>
        <View>
          <Text style={styles.title} allowFontScaling={false}>
            {format(parseISO(day.Date), 'EEEE d MMMM yyyy')}
          </Text>
          <Text style={styles.caption} allowFontScaling={false}>
            {day.WeekTitle}
          </Text>
          {day.Event && (
            <>
              <Text style={[styles.caption2, dropdownStyle]} allowFontScaling={false} numberOfLines={1}>
                {day.Event[0].data}{eventShouldShowOption && ` (${day.Event[0].option})`}
              </Text>
            </>
          )}
        </View>
        <View style={styles.bottomContainer}>
          {day.Colour ? (
            <View
              style={[
                styles.color,
                // eslint-disable-next-line react-native/no-inline-styles
                {
                  height: day.Colour === 'White' ? 18 : 16,
                  backgroundColor: backgroundHex ? backgroundHex : 'grey',
                  borderWidth: day.Colour === 'White' ? 1 : 0,
                },
              ]}>
              <Text style={[styles.colorText, { color: textColor }]} allowFontScaling={false}>
                {day.Colour}
              </Text>
            </View>
          ) : (
            <View />
          )}
          <View style={{ flexDirection: 'row' }}>
            {noted && <Image style={styles.icon} source={images.Button_Notes_Active} />}
            {bookmarked && <Image style={styles.icon} source={images.Button_Bookmark_Active} />}
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );
};

const BookmarksScreen = ({ navigation }) => {
  const flatList = useRef(null);
  const toast = useToast();
  const context = useContext(AppContext);

  const { days } = context;

  const [bookmarkedDays, setBookmarkedDays] = useState([]);
  const [extraData, setExtraData] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    getBookmarks();
  }, []);

  useEffect(() => {
    getNotes();
  }, []);

  useEffect(() => {
    EventRegister.addEventListener('bookmarks/updated', getBookmarks);
    EventRegister.addEventListener('notes/updated', getNotes);

    return () => {
      EventRegister.removeEventListener('bookmarks/updated');
      EventRegister.removeEventListener('notes/updated');
    }
  }, []);

  const getBookmarks = async () => {
    const _bookmarks = await DataController.getBookmarks();

    setBookmarks(_bookmarks);
    setBookmarkedDays(days.filter(day => _bookmarks.includes(day.id)));
    setExtraData(!extraData);
  };

  const getNotes = async () => {
    const _notes = await DataController.getAllNotes();

    setNotes(_notes);
  };

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <View style={styles.headerRow}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Image style={styles.menuIcon} source={images.Button_Back} />
            <Text style={styles.backButtonText} allowFontScaling={false}>
              {'Back'}
            </Text>
          </TouchableOpacity>
        </View>
      ),
      headerTitle: () => (
        <Text style={styles.headerTitle} allowFontScaling={false}>
          {'Bookmarks'}
        </Text>
      ),
      headerTitleAlign: 'center',
      // headerRight: () => (
      //   <View style={styles.headerRow}>
      //     <TouchableOpacity style={styles.buttonMenu} onPress={() => filterPressed()}>
      //       <Image style={styles.menuIcon} source={images.Button_Filter} />
      //     </TouchableOpacity>
      //     <TouchableOpacity style={styles.buttonMenu} onPress={() => calendarPressed()}>
      //       <Image style={styles.menuIcon} source={images.Button_Calendar_Red} />
      //     </TouchableOpacity>
      //   </View>
      // ),
      headerStyle: {
        backgroundColor: colors.grey20,
        shadowRadius: 0,
        shadowOffset: {
          height: 0,
        },
        borderBottomWidth: 0.5,
        borderBottomColor: colors.grey50,
        height: Platform.OS === 'web' ? 44 : undefined,
      },
    });
  }, [navigation, filterPressed, calendarPressed]);

  const filterPressed = useCallback((value) => {
    __DEV__ && console.log('filtersOpen', value)
    // toast.show('Not implemented', { id: Date.now() });
  }, [toast]);

  const calendarPressed = useCallback(() => {
    toast.show('Not implemented', { id: Date.now() });
  }, [toast]);

  const openMenu = useCallback(() => {
    EventRegister.emit('menu/toggle-contents');
  }, []);

  const renderItem = ({ item, index }) => {
    return <Row day={item} notes={notes} bookmarks={bookmarks} />;
  };

  const keyExtractor = (item, index) => `${item.Date}-${index}`;

  if (bookmarkedDays.length === 0) {
    return (
      <View style={[styles.emptyList, { height: Dimensions.get('window').height }]}>
        <Text style={styles.emptyListText} allowFontScaling={false}>
          {'No bookmarks'}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlashList
        ref={flatList}
        data={bookmarkedDays}
        extraData={extraData}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        estimatedItemSize={126}
        ItemSeparatorComponent={renderSeparator}
        initialNumToRender={10}
        removeClippedSubviews={false}
        contentContainerStyle={{ paddingBottom: 54 }}
        indicatorStyle="black"
        ListEmptyComponent={() => (
          <View style={[styles.emptyList, { height: Dimensions.get('window').height }]}>
            <Text style={styles.emptyListText} allowFontScaling={false}>
              {'No bookmarks'}
            </Text>
          </View>
        )}
      />
    </View>
  );
};

const renderSeparator = () => (
  <View
    style={[styles.separator, {
      width: isTablet() || Platform.OS === 'web'
        ? Dimensions.get('window').width - 60
        : Dimensions.get('window').width - 30,
    }]}
  />
);


const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.accentWhite,
    flex: 1,
    height: '100%',
  },
  scrollViewContainer: {
    flexGrow: 1,
    paddingHorizontal: isTablet() || Platform.OS === 'web' ? 30 : 15,
    paddingBottom: 54,
    width: '100%',
    maxWidth: 1400,
    alignSelf: 'center',
  },
  backButton: {
    flexDirection: 'row',
    marginStart: 4,
    alignItems: 'center',
  },
  backButtonText: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 17,
    fontWeight: 'normal',
    color: colors.primary,
    marginStart: -5,
  },
  headerRow: {
    flexDirection: 'row',
  },
  buttonMenu: {
    width: 44,
    height: 44,
    marginStart: 4,
  },
  menuIcon: {
    width: 44,
    height: 44,
    tintColor: colors.primary,
  },
  browseRow: {
    height: 124,
    justifyContent: 'space-between',
    paddingTop: 12,
    paddingBottom: 4,
  },
  separator: {
    height: 2,
    backgroundColor: colors.grey30,
    maxWidth: 1400,
    alignSelf: 'center',
  },
  title: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 20,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 28,
    letterSpacing: -0.1,
    color: colors.defaultText,
    marginBottom: 4,
    maxWidth: '80%',
  },
  color: {
    right: 0,
    top: 0,
    borderRadius: 9,
    backgroundColor: '#009900',
    height: 16,
    paddingHorizontal: 6,
    justifyContent: 'center',
    alignSelf: 'center',
  },
  colorText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 12,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    letterSpacing: -0.1,
    color: colors.accentWhite,
  },
  caption: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 12,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    letterSpacing: 0.2,
    color: colors.defaultText,
  },
  caption2: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 11,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 14,
    letterSpacing: 0.1,
    color: colors.defaultText,
    marginTop: 5,
  },
  bottomContainer: {
    height: 44,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  icon: {
    width: 44,
    height: 44,
    marginStart: 5,
  },
  emptyList: {
    flex: 1,
    backgroundColor: colors.accentWhite,
    justifyContent: 'center',
  },
  emptyListText: {
    alignSelf: 'center',
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 20,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 28,
    letterSpacing: -0.1,
    color: colors.grey60,
  },
  headerTitle: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    textAlign: 'center',
    color: colors.primary,
  },
});

export default BookmarksScreen;
